import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBridgeState } from '@digitaz/bridge-sdk-client';
import { useRouter } from 'next/router';
import { AgencyFilterHeader } from './AgencyFilterHeader';
import { AgencyFilterResults } from './AgencyFilterResults';
import { scrollTop } from '../../../utils/helpers/smoothScroll';
import { AgencyFilterContainer } from './styled';
import { buildAgencyUrl } from '../../../utils/helpers/agencyPage';
import { AgencyFilterProps } from './types';
import {
  AGENCY_FILTER_ALL_DEAL_TYPES,
  AGENCY_FILTER_CATEGORY_ALL_ID,
  AgencyFilterDealTypeEnum
} from '../../../utils/constants/agencyPage';

export const AgencyFilter: React.FC<AgencyFilterProps> = ({
  categories = [],
  allCategories = [],
  agencyId,
  isCurrentItemPage = false,
  initialFilter
}) => {
  const { t } = useTranslation();
  const { isWebView } = useBridgeState();
  const router = useRouter();
  const wrapperRef = useRef<HTMLElement | null>(null);
  const categoriesContainerRef = useRef<HTMLDivElement>(null);
  const [category, setCategory] = useState(() => {
    if (initialFilter?.category) {
      return { id: initialFilter.category };
    }

    if (initialFilter?.dealType) {
      return { id: initialFilter.dealType };
    }

    return { id: AGENCY_FILTER_CATEGORY_ALL_ID };
  });

  const [activeLeasedCategory, setActiveLeasedCategory] = useState(() => {
    if (initialFilter?.dealType && initialFilter?.category) {
      return [initialFilter.dealType];
    }

    return AGENCY_FILTER_ALL_DEAL_TYPES;
  });

  const combinedCategories = [
    {
      id: AgencyFilterDealTypeEnum.lease,
      pluralName: t('pages.agency.lease'),
      slug: 'lease'
    },
    {
      id: AgencyFilterDealTypeEnum.sale,
      pluralName: t('pages.agency.sale'),
      slug: 'sale'
    },
    ...categories
  ].map(categoryItem => {
    const found = allCategories
      .flatMap(item => (item.children.length ? item.children : [item]))
      .find(child => child.id === categoryItem.id);

    return {
      ...categoryItem,
      title: found ? found.title : null
    };
  });

  const bottomOffset = isWebView ? 50 : 20;

  const scrollToSelectedOption = (id: string) => {
    const chipEl = document.getElementById(`chip-${id}`);

    if (chipEl && categoriesContainerRef.current) {
      const container = categoriesContainerRef.current;
      const chipLeft = chipEl.offsetLeft;
      const chipWidth = chipEl.offsetWidth;
      const containerWidth = container.offsetWidth;
      const scrollLeft = chipLeft - containerWidth / 2 + chipWidth / 2;
      container.scrollLeft = scrollLeft;
    }
  };

  useEffect(() => {
    if (initialFilter?.dealType || initialFilter?.category) {
      scrollToSelectedOption(category.id);
    }
  }, [initialFilter, category.id]);

  const onClickFilterItem = (
    id: string,
    isSetLeasedCategories = true,
    overrideLeasedCategory?: AgencyFilterDealTypeEnum[]
  ) => {
    scrollToSelectedOption(id);

    if (wrapperRef.current) {
      const offsetTop =
        wrapperRef.current.offsetTop - (isCurrentItemPage ? bottomOffset : 0);

      scrollTop(
        document.documentElement || document.scrollingElement || document.body,
        offsetTop
      );
    }

    let nextLeasedCategory = activeLeasedCategory;

    setCategory({ id });
    if (overrideLeasedCategory) {
      nextLeasedCategory = overrideLeasedCategory;
      setActiveLeasedCategory(overrideLeasedCategory);
    } else if (isSetLeasedCategories) {
      nextLeasedCategory = AGENCY_FILTER_ALL_DEAL_TYPES;
      setActiveLeasedCategory(AGENCY_FILTER_ALL_DEAL_TYPES);
    }

    if (!isCurrentItemPage) {
      const url = buildAgencyUrl({
        agencySlug: router.query.agency as string,
        clickedId: id,
        activeLeasedCategory: nextLeasedCategory,
        categories
      });

      router.push(url, undefined, {
        shallow: true,
        scroll: false
      });
    }
  };

  return (
    <AgencyFilterContainer data-cy="agency-filter" ref={wrapperRef}>
      <AgencyFilterHeader
        categories={combinedCategories}
        activeCategoryId={category.id.toString()}
        onClickItem={onClickFilterItem}
        categoriesContainerRef={categoriesContainerRef}
        isCurrentItemPage={isCurrentItemPage}
      />
      <AgencyFilterResults
        agencyId={agencyId}
        categories={combinedCategories}
        activeCategoryId={category.id.toString()}
        onClickItem={onClickFilterItem}
        activeLeasedCategory={activeLeasedCategory}
        isCurrentItemPage={isCurrentItemPage}
      />
    </AgencyFilterContainer>
  );
};
